import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import client components (lazy-loaded for performance)
const ClientsList = React.lazy(() => import("./ClientsList"));
const NewClientForm = React.lazy(() => import("./NewClientForm"));
const ClientDetails = React.lazy(() => import("./ClientDetails"));
const Permissions = React.lazy(() => import("./Permissions"));
const MergeClients = React.lazy(() => import("./MergeClients"));

/**
 * Clients Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (clients/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "delete", "permissions", "merge")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/clients/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const clientsModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "clients",
    name: "Client Management",
    description: "Manage client information, contacts, and relationships",
    version: "1.2.0",
    enabled: true,
    category: "crm",
    order: 2,
    icon: "👥",
    color: "#38A169",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/clients",
            component: ClientsList,
            permissionLevel: "view",
        },
        {
            path: "/newclient",
            component: NewClientForm,
            permissionLevel: "create",
        },
        {
            path: "/client/:id",
            component: ClientDetails,
            permissionLevel: "view",
        },
        {
            path: "/clients/permissions",
            component: Permissions,
            permissionLevel: "permissions",
        },
        {
            path: "/clients/merge",
            component: MergeClients,
            permissionLevel: "merge",
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Clients",
            path: "/clients",
            icon: "👥",
            permissionLevel: "view",
        },
        {
            label: "New Client",
            path: "/newclient",
            icon: "➕",
            permissionLevel: "create",
        },
        {
            label: "Permissions",
            path: "/clients/permissions",
            icon: "🔐",
            permissionLevel: "permissions",
        },
        {
            label: "Merge Clients",
            path: "/clients/merge",
            icon: "🔀",
            permissionLevel: "merge",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/clients/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
        permissions: [],
        merge: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Add Client",
            path: "/newclient",
            icon: "➕",
            description: "Add a new client to your database"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "1.2.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready client management system",
                "✅ Advanced search and filtering capabilities",
                "✅ Tag-based organization system",
                "✅ Permission management system",
                "✅ Comprehensive client profiles",
                "✅ Full TypeScript type safety"
            ],
            notes: "Official stable release with complete CRM functionality and production-ready features."
        },
        {
            version: "1.1.0",
            releaseDate: "2025-09-23",
            title: "Data Quality & UI Refinements",
            features: [
                "✨ Added input trimming to ClientDetails form submission",
                "🐛 Fixed whitespace handling in client data",
                "✨ Enhanced navigation consistency across client pages",
                "✨ Improved video management integration"
            ],
            notes: "Data quality improvements and UI consistency enhancements."
        },
        {
            version: "1.0.0",
            releaseDate: "2025-09-20",
            title: "Theme System & Color Mode",
            features: [
                "✨ Full light/dark mode support across all client components",
                "✨ Enhanced ClientDetails with theme-aware styling",
                "✨ Updated ClientsList with color mode enhancements",
                "✨ Improved NewClientForm with theme consistency",
                "✨ Enhanced Permissions page with theme support"
            ],
            notes: "Major UI overhaul with comprehensive theme system implementation."
        },
        {
            version: "0.9.0",
            releaseDate: "2025-09-13",
            title: "Permission Management & Navigation",
            features: [
                "✨ Added client permission management to ClientsList",
                "✨ Introduced ClickablePermissionBadge component",
                "✨ Added ModuleBreadcrumb navigation to all client pages",
                "✨ Enhanced GraphQL code generation support",
                "✨ Fixed nullable field handling",
                "🔒 Improved permission-based UI rendering"
            ],
            notes: "Comprehensive permission management system with improved navigation."
        },
        {
            version: "0.8.0",
            releaseDate: "2025-09-12",
            title: "Tag Management & UI Refactoring",
            features: [
                "✨ Implemented comprehensive tag management system",
                "✨ Added tag filtering to ClientsList",
                "🔨 Major refactoring of ClientDetails (638 lines removed)",
                "✨ Removed legacy tag management from ClientDetails",
                "✨ Enhanced UI consistency across components",
                "✨ Improved component performance"
            ],
            notes: "Major refactoring improving maintainability and adding tag-based organization."
        },
        {
            version: "0.7.0",
            releaseDate: "2025-09-09",
            title: "Advanced Search & Filtering",
            features: [
                "✨ Added ClientSearchSelector component",
                "✨ Implemented advanced client search functionality",
                "✨ Added tag-based filtering capabilities",
                "✨ Enhanced client selection workflow",
                "✨ Improved search performance and UX"
            ],
            notes: "Advanced search and filtering features for better client discovery."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-08-25",
            title: "Integration Enhancements",
            features: [
                "✨ Enhanced Permissions component UI",
                "✨ Improved calendar integration",
                "✨ Added email inbox integration support",
                "✨ Enhanced address book compatibility"
            ],
            notes: "Improved integrations with other system modules."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-08-08",
            title: "Security & Brand Consistency",
            features: [
                "🔒 Implemented protected routes for client management",
                "✨ Enhanced brand configuration integration",
                "✨ Improved UI consistency across components",
                "🔒 Added authentication checks to all client pages",
                "✨ Enhanced responsive design"
            ],
            notes: "Security improvements and brand consistency enhancements."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-08-02",
            title: "Permissions System Launch",
            features: [
                "✅ Created Permissions management page (325 lines)",
                "✅ Added permission-based access control",
                "✅ Implemented role management for clients",
                "✅ Added permissions to moduleConfig routing",
                "🔒 Enhanced security with granular permissions"
            ],
            notes: "Launch of comprehensive permissions management system."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-07-14",
            title: "Module Configuration",
            features: [
                "✅ Created moduleConfig.ts structure",
                "✅ Defined routes and navigation",
                "✅ Configured permissions system",
                "✅ Added module metadata and icons",
                "✅ Integrated with profile management"
            ],
            notes: "Established formal module configuration architecture."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-06-17",
            title: "Feature Expansion & Enhancements",
            features: [
                "✨ Enhanced ClientDetails with expanded functionality",
                "✨ Improved ClientsList with better filtering",
                "✨ Major enhancements to NewClientForm (303 lines)",
                "✨ Added lucide-react icon library",
                "✨ Enhanced routing capabilities"
            ],
            notes: "Major feature expansion with enhanced forms and functionality."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-06-14",
            title: "Initial Client Management System",
            features: [
                "✅ Created ClientDetails component (505 lines)",
                "✅ Created ClientsList component (437 lines)",
                "✅ Created NewClientForm component (228 lines)",
                "✅ Basic CRUD operations for clients",
                "✅ Initial GraphQL integration",
                "✅ Core client management functionality"
            ],
            notes: "Initial implementation of the client management system with core CRM features."
        }
    ],

    // ROADMAP
    roadmap: []
};

export default clientsModuleConfig;
